import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Calculator, Droplets, AlertTriangle, CheckCircle, ShoppingCart, Info, Beaker, Waves, Sparkles, User, Settings } from 'lucide-react';
import { apiRequest, formatPrice } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import SEOHead from '../components/SEOHead';

// Dosage data for different treatments (per 10m³) - Produits CTX PRO
const DOSAGES = {
    chloreLent: {
        name: 'CTX PRO Chlore Lent',
        description: 'Traitement de fond pour maintenir le chlore - Galets 250g à dissolution lente',
        dosePer10m3: 200, // grams per 10m³ per week
        unit: 'g',
        frequency: 'par semaine',
        productIds: ['oca-chlore-lent'],
        icon: Droplets,
        color: 'text-blue-500'
    },
    chloreChoc: {
        name: 'CTX PRO Chlore Choc',
        description: 'Traitement choc pour eau verte ou trouble - Pastilles 20g',
        dosePer10m3: 150, // grams per 10m³
        unit: 'g',
        frequency: 'traitement unique',
        productIds: ['oca-chlore-choc'],
        icon: AlertTriangle,
        color: 'text-orange-500'
    },
    chloreRapide: {
        name: 'CTX PRO Chlore Rapide V60',
        description: 'Action rapide, granulés à dissolution immédiate',
        dosePer10m3: 100, // grams per 10m³
        unit: 'g',
        frequency: 'selon besoin',
        productIds: ['oca-chlore-rapide'],
        icon: Sparkles,
        color: 'text-yellow-500'
    },
    pchGranule: {
        name: 'CTX PRO PCH Granulé',
        description: 'Chlore non stabilisé, idéal pour piscines au sel ou intérieures',
        dosePer10m3: 150, // grams per 10m³
        unit: 'g',
        frequency: 'selon besoin',
        productIds: ['oca-pch-granule', 'oca-pch-longue-duree'],
        icon: Beaker,
        color: 'text-cyan-500'
    },
    force5: {
        name: 'CTX PRO Force 5 Multifonction',
        description: '5 actions en 1 : chlore, anti-algues, floculant, stabilisant, anti-calcaire',
        dosePer10m3: 250, // grams per 10m³ per week
        unit: 'g',
        frequency: 'par semaine',
        productIds: ['oca-force5'],
        icon: Waves,
        color: 'text-purple-500'
    },
    brome: {
        name: 'CTX PRO Brome Lent',
        description: 'Alternative au chlore, idéal pour peaux sensibles et spas',
        dosePer10m3: 150, // grams per 10m³ per week
        unit: 'g',
        frequency: 'par semaine',
        productIds: ['oca-brome-lent'],
        icon: Droplets,
        color: 'text-pink-500'
    },
    oxyspeed: {
        name: 'CTX PRO OxySpeed',
        description: 'Traitement à l\'oxygène actif, sans chlore',
        dosePer10m3: 500, // ml per 10m³
        unit: 'ml',
        frequency: 'par semaine',
        productIds: ['oca-oxyspeed'],
        icon: Sparkles,
        color: 'text-indigo-500'
    },
    phMinus: {
        name: 'CTX PRO pH Minus',
        description: 'Réduire le pH - Acide sulfurique professionnel',
        dosePer10m3: 100, // ml per 10m³ to lower pH by 0.2
        unit: 'ml',
        frequency: 'pour -0.2 pH',
        productIds: ['oca-ph-minus'],
        icon: Droplets,
        color: 'text-red-500'
    },
    antiAlgues: {
        name: 'CTX PRO Anti-Algues QT20',
        description: 'Prévention et traitement curatif des algues vertes',
        dosePer10m3: 100, // ml per 10m³ (preventive)
        doseCurativePer10m3: 300, // ml per 10m³ (curative)
        unit: 'ml',
        frequency: 'par mois (préventif)',
        productIds: ['oca-anti-algues', 'oca-algistop'],
        icon: Beaker,
        color: 'text-emerald-500'
    },
    algistop: {
        name: 'CTX PRO Algi-Stop 3',
        description: 'Anti-algues concentré triple action (vert, moutarde, noire)',
        dosePer10m3: 50, // ml per 10m³ (preventive)
        unit: 'ml',
        frequency: 'par quinzaine',
        productIds: ['oca-algistop3'],
        icon: Beaker,
        color: 'text-lime-500'
    },
    floculant: {
        name: 'CTX PRO Floculant',
        description: 'Clarifier l\'eau trouble - Cartouches ou Flovil',
        dosePer10m3: 1, // cartridge per 25m³
        unit: 'cartouche',
        frequency: 'par mois (filtre sable)',
        productIds: ['oca-floculant', 'oca-flovil'],
        icon: Sparkles,
        color: 'text-sky-500'
    },
    antiCalcaire: {
        name: 'CTX PRO Anti-Calcaire',
        description: 'Prévenir les dépôts de calcaire et le tartre',
        dosePer10m3: 100, // ml per 10m³
        unit: 'ml',
        frequency: 'par mois',
        productIds: ['oca-anti-calcaire'],
        icon: Waves,
        color: 'text-teal-500'
    },
    metalfix: {
        name: 'CTX PRO Métalfix',
        description: 'Séquestrant métaux - Évite les taches de fer/cuivre',
        dosePer10m3: 200, // grams per 10m³
        unit: 'g',
        frequency: 'au remplissage',
        productIds: ['oca-metalfix'],
        icon: Beaker,
        color: 'text-amber-500'
    }
};

// Pool volume calculator formulas
const POOL_SHAPES = {
    rectangle: {
        name: 'Rectangle',
        formula: (l, w, d) => l * w * d,
        fields: ['length', 'width', 'depth']
    },
    oval: {
        name: 'Ovale',
        formula: (l, w, d) => l * w * d * 0.89,
        fields: ['length', 'width', 'depth']
    },
    round: {
        name: 'Ronde',
        formula: (d, _, depth) => Math.PI * Math.pow(d / 2, 2) * depth,
        fields: ['diameter', 'depth']
    },
    freeform: {
        name: 'Forme libre',
        formula: (l, w, d) => l * w * d * 0.85,
        fields: ['length', 'width', 'depth']
    }
};

export default function CalculatorPage() {
    const { user } = useAuth();
    const [volume, setVolume] = useState('');
    const [selectedTreatment, setSelectedTreatment] = useState('chloreLent');
    const [calculatedDose, setCalculatedDose] = useState(null);
    const [products, setProducts] = useState([]);
    const [activeTab, setActiveTab] = useState('dosage');
    const [poolProfile, setPoolProfile] = useState(null);
    const [profileLoaded, setProfileLoaded] = useState(false);
    
    // Volume calculator state
    const [poolShape, setPoolShape] = useState('rectangle');
    const [dimensions, setDimensions] = useState({ length: '', width: '', diameter: '', depth: '' });
    const [calculatedVolume, setCalculatedVolume] = useState(null);

    // Fetch pool profile if user is logged in
    useEffect(() => {
        const fetchPoolProfile = async () => {
            if (!user) {
                setProfileLoaded(true);
                return;
            }
            try {
                const profile = await apiRequest('/pool-config/profile');
                if (profile && profile.volume) {
                    setPoolProfile(profile);
                    setVolume(profile.volume.toString());
                    // Also set dimensions if available
                    if (profile.length) setDimensions(d => ({ ...d, length: profile.length.toString() }));
                    if (profile.width) setDimensions(d => ({ ...d, width: profile.width.toString() }));
                    if (profile.depth_avg) setDimensions(d => ({ ...d, depth: profile.depth_avg.toString() }));
                }
            } catch (error) {
                // Profile not found or error - that's okay
                console.log('No pool profile found');
            }
            setProfileLoaded(true);
        };
        fetchPoolProfile();
    }, [user]);

    // Fetch products on mount
    useEffect(() => {
        const fetchProducts = async () => {
            try {
                const data = await apiRequest('/products');
                setProducts(data);
            } catch (error) {
                console.error('Error fetching products:', error);
            }
        };
        fetchProducts();
    }, []);

    // Calculate dose when volume or treatment changes
    useEffect(() => {
        if (volume && parseFloat(volume) > 0) {
            const treatment = DOSAGES[selectedTreatment];
            const vol = parseFloat(volume);
            const dose = (treatment.dosePer10m3 * vol) / 10;
            setCalculatedDose({
                amount: Math.round(dose),
                unit: treatment.unit,
                frequency: treatment.frequency,
                treatment: treatment
            });
        } else {
            setCalculatedDose(null);
        }
    }, [volume, selectedTreatment]);

    // Calculate pool volume
    const calculatePoolVolume = () => {
        const shape = POOL_SHAPES[poolShape];
        const l = parseFloat(dimensions.length) || parseFloat(dimensions.diameter) || 0;
        const w = parseFloat(dimensions.width) || 0;
        const d = parseFloat(dimensions.depth) || 0;
        
        if (poolShape === 'round') {
            if (l > 0 && d > 0) {
                const vol = shape.formula(l, 0, d);
                setCalculatedVolume(Math.round(vol * 10) / 10);
                setVolume(Math.round(vol).toString());
            }
        } else {
            if (l > 0 && w > 0 && d > 0) {
                const vol = shape.formula(l, w, d);
                setCalculatedVolume(Math.round(vol * 10) / 10);
                setVolume(Math.round(vol).toString());
            }
        }
    };

    // Get matching products for the selected treatment
    const getMatchingProducts = () => {
        if (!calculatedDose) return [];
        const treatment = DOSAGES[selectedTreatment];
        return products.filter(p => 
            treatment.productIds.some(id => p.product_id?.includes(id.split('-').slice(1).join('-')))
        );
    };

    const matchingProducts = getMatchingProducts();
    const TreatmentIcon = DOSAGES[selectedTreatment]?.icon || Droplets;

    return (
        <>
            <SEOHead 
                title="Calculateur de Dosage Piscine - Chlore, pH, Anti-Algues | O.C.A Piscine"
                description="Calculez gratuitement les doses exactes de produits pour votre piscine : chlore lent, chlore choc, pH+, pH-, anti-algues, floculant. Entrez le volume de votre bassin et obtenez un dosage précis avec les produits CTX PRO."
                keywords="calculateur dosage piscine, dose chlore piscine, quantité chlore, dosage pH piscine, calculateur chlore, traitement piscine dosage, produit piscine quantité"
                canonicalUrl="https://www.ocapiscine.fr/calculateur"
            />
            
            <div className="min-h-screen pt-20 pb-12" data-testid="calculator-page">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    {/* Header */}
                    <div className="text-center mb-8">
                        <div className="flex items-center justify-center gap-3 mb-2">
                            <Calculator className="h-8 w-8 text-primary" />
                            <h1 className="font-heading text-2xl sm:text-3xl font-bold">Calculateur de Dosage</h1>
                        </div>
                        <p className="text-muted-foreground">
                            Calculez les doses exactes de produits pour votre piscine
                        </p>
                    </div>

                    <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
                        <TabsList className="grid w-full grid-cols-2">
                            <TabsTrigger value="dosage" data-testid="tab-dosage">
                                <Beaker className="h-4 w-4 mr-2" />
                                Calculer un dosage
                            </TabsTrigger>
                            <TabsTrigger value="volume" data-testid="tab-volume">
                                <Waves className="h-4 w-4 mr-2" />
                                Calculer le volume
                            </TabsTrigger>
                        </TabsList>

                        {/* Dosage Calculator Tab */}
                        <TabsContent value="dosage" className="space-y-6">
                            {/* Pool Profile Banner - if user is logged in and has profile */}
                            {user && poolProfile && (
                                <div className="bg-primary/5 border border-primary/20 rounded-xl p-4 flex items-center gap-4">
                                    <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                                        <User className="h-5 w-5 text-primary" />
                                    </div>
                                    <div className="flex-1">
                                        <p className="font-medium text-sm">Votre piscine : {poolProfile.volume} m³</p>
                                        <p className="text-xs text-muted-foreground">
                                            {poolProfile.pool_type && `${poolProfile.pool_type} • `}
                                            {poolProfile.treatment_system && `Traitement ${poolProfile.treatment_system}`}
                                        </p>
                                    </div>
                                    <Link to="/pool-config">
                                        <Button variant="outline" size="sm" className="rounded-full">
                                            <Settings className="h-4 w-4 mr-1" />
                                            Modifier
                                        </Button>
                                    </Link>
                                </div>
                            )}

                            {/* Prompt to configure pool if user is logged in but has no profile */}
                            {user && profileLoaded && !poolProfile && (
                                <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 flex items-center gap-4">
                                    <div className="h-10 w-10 rounded-full bg-amber-500/10 flex items-center justify-center">
                                        <Info className="h-5 w-5 text-amber-500" />
                                    </div>
                                    <div className="flex-1">
                                        <p className="font-medium text-sm text-amber-700 dark:text-amber-400">Configurez votre piscine</p>
                                        <p className="text-xs text-muted-foreground">
                                            Enregistrez votre piscine pour pré-remplir automatiquement le volume
                                        </p>
                                    </div>
                                    <Link to="/pool-config">
                                        <Button variant="outline" size="sm" className="rounded-full border-amber-500/50 text-amber-600 hover:bg-amber-500/10">
                                            Configurer
                                        </Button>
                                    </Link>
                                </div>
                            )}

                            {/* Volume Input */}
                            <Card className="glass">
                                <CardHeader>
                                    <CardTitle className="text-lg flex items-center gap-2">
                                        <Waves className="h-5 w-5 text-primary" />
                                        Volume de votre piscine
                                        {poolProfile && (
                                            <Badge variant="secondary" className="ml-2 text-xs">
                                                Pré-rempli
                                            </Badge>
                                        )}
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="flex items-center gap-4">
                                        <div className="flex-1">
                                            <Input
                                                type="number"
                                                placeholder="Ex: 50"
                                                value={volume}
                                                onChange={(e) => setVolume(e.target.value)}
                                                className="text-lg h-12"
                                                data-testid="volume-input"
                                            />
                                        </div>
                                        <span className="text-lg font-medium text-muted-foreground">m³</span>
                                    </div>
                                    <p className="text-sm text-muted-foreground mt-2">
                                        {!user ? (
                                            <>
                                                <Link to="/auth" className="text-primary hover:underline">Connectez-vous</Link>
                                                {' '}pour enregistrer votre piscine et pré-remplir automatiquement
                                            </>
                                        ) : (
                                            <>
                                                Vous ne connaissez pas le volume ? 
                                                <Button 
                                                    variant="link" 
                                                    className="px-1 h-auto" 
                                                    onClick={() => setActiveTab('volume')}
                                                >
                                                    Calculez-le ici
                                                </Button>
                                            </>
                                        )}
                                    </p>
                                </CardContent>
                            </Card>

                            {/* Treatment Selection */}
                            <Card className="glass">
                                <CardHeader>
                                    <CardTitle className="text-lg flex items-center gap-2">
                                        <Beaker className="h-5 w-5 text-primary" />
                                        Type de traitement
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <Select value={selectedTreatment} onValueChange={setSelectedTreatment}>
                                        <SelectTrigger className="h-12" data-testid="treatment-select">
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {Object.entries(DOSAGES).map(([key, treatment]) => {
                                                const Icon = treatment.icon;
                                                return (
                                                    <SelectItem key={key} value={key}>
                                                        <div className="flex items-center gap-2">
                                                            <Icon className={`h-4 w-4 ${treatment.color}`} />
                                                            <span>{treatment.name}</span>
                                                        </div>
                                                    </SelectItem>
                                                );
                                            })}
                                        </SelectContent>
                                    </Select>
                                    <p className="text-sm text-muted-foreground mt-2">
                                        {DOSAGES[selectedTreatment]?.description}
                                    </p>
                                </CardContent>
                            </Card>

                            {/* Results */}
                            {calculatedDose && (
                                <Card className="glass border-primary/50" data-testid="dose-result">
                                    <CardHeader>
                                        <CardTitle className="text-lg flex items-center gap-2">
                                            <CheckCircle className="h-5 w-5 text-emerald-500" />
                                            Dosage recommandé
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent className="space-y-4">
                                        <div className="text-center py-6 bg-primary/5 rounded-xl">
                                            <div className="flex items-center justify-center gap-2 mb-2">
                                                <TreatmentIcon className={`h-8 w-8 ${calculatedDose.treatment.color}`} />
                                            </div>
                                            <p className="text-4xl font-bold text-primary">
                                                {calculatedDose.amount} {calculatedDose.unit}
                                            </p>
                                            <p className="text-muted-foreground mt-1">
                                                de {calculatedDose.treatment.name}
                                            </p>
                                            <Badge variant="secondary" className="mt-2">
                                                {calculatedDose.frequency}
                                            </Badge>
                                        </div>

                                        <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-4">
                                            <div className="flex items-start gap-2">
                                                <Info className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
                                                <div className="text-sm">
                                                    <p className="font-medium text-amber-600 dark:text-amber-400">Conseils d'utilisation</p>
                                                    <ul className="mt-1 text-muted-foreground space-y-1">
                                                        <li>• Versez le produit devant les buses de refoulement</li>
                                                        <li>• Filtration en marche pendant 2-4 heures</li>
                                                        <li>• Ne jamais mélanger les produits entre eux</li>
                                                        <li>• Attendez 4h avant de vous baigner</li>
                                                    </ul>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Matching products */}
                                        {matchingProducts.length > 0 && (
                                            <div className="pt-4 border-t">
                                                <h4 className="font-medium mb-3 flex items-center gap-2">
                                                    <ShoppingCart className="h-4 w-4" />
                                                    Produits recommandés
                                                </h4>
                                                <div className="grid gap-3">
                                                    {matchingProducts.slice(0, 3).map(product => (
                                                        <Link 
                                                            key={product.product_id} 
                                                            to={`/boutique/${product.product_id}`}
                                                            className="flex items-center gap-3 p-3 rounded-lg bg-muted/50 hover:bg-muted transition-colors"
                                                        >
                                                            {product.image_url && (
                                                                <img 
                                                                    src={product.image_url} 
                                                                    alt={product.name}
                                                                    className="h-12 w-12 object-cover rounded"
                                                                />
                                                            )}
                                                            <div className="flex-1 min-w-0">
                                                                <p className="font-medium truncate">{product.name}</p>
                                                                <p className="text-sm text-primary font-semibold">
                                                                    {formatPrice(product.price)}
                                                                </p>
                                                            </div>
                                                            <Button size="sm" variant="outline">
                                                                Voir
                                                            </Button>
                                                        </Link>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </CardContent>
                                </Card>
                            )}

                            {/* Info Card */}
                            <Card className="glass">
                                <CardContent className="pt-6">
                                    <div className="flex items-start gap-3">
                                        <Info className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                                        <div className="text-sm text-muted-foreground">
                                            <p className="font-medium text-foreground mb-1">À propos des dosages</p>
                                            <p>
                                                Ces dosages sont indicatifs et basés sur des conditions moyennes. 
                                                Les quantités peuvent varier selon la température, l'ensoleillement, 
                                                la fréquentation et l'état initial de l'eau.
                                            </p>
                                            <p className="mt-2">
                                                Pour un conseil personnalisé, utilisez notre{' '}
                                                <Link to="/chat" className="text-primary hover:underline">
                                                    Assistant IA
                                                </Link>
                                                {' '}ou contactez-nous au{' '}
                                                <a href="tel:0651961474" className="text-primary hover:underline">
                                                    06 51 96 14 74
                                                </a>
                                            </p>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        </TabsContent>

                        {/* Volume Calculator Tab */}
                        <TabsContent value="volume" className="space-y-6">
                            <Card className="glass">
                                <CardHeader>
                                    <CardTitle className="text-lg flex items-center gap-2">
                                        <Waves className="h-5 w-5 text-primary" />
                                        Calculateur de volume
                                    </CardTitle>
                                    <CardDescription>
                                        Entrez les dimensions de votre piscine pour calculer son volume
                                    </CardDescription>
                                </CardHeader>
                                <CardContent className="space-y-6">
                                    {/* Pool Shape */}
                                    <div className="space-y-2">
                                        <Label>Forme de la piscine</Label>
                                        <Select value={poolShape} onValueChange={setPoolShape}>
                                            <SelectTrigger data-testid="shape-select">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {Object.entries(POOL_SHAPES).map(([key, shape]) => (
                                                    <SelectItem key={key} value={key}>
                                                        {shape.name}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>

                                    {/* Dimensions */}
                                    <div className="grid gap-4">
                                        {poolShape === 'round' ? (
                                            <>
                                                <div className="space-y-2">
                                                    <Label>Diamètre (mètres)</Label>
                                                    <Input
                                                        type="number"
                                                        step="0.1"
                                                        placeholder="Ex: 5"
                                                        value={dimensions.diameter}
                                                        onChange={(e) => setDimensions({...dimensions, diameter: e.target.value})}
                                                        data-testid="diameter-input"
                                                    />
                                                </div>
                                            </>
                                        ) : (
                                            <>
                                                <div className="grid grid-cols-2 gap-4">
                                                    <div className="space-y-2">
                                                        <Label>Longueur (mètres)</Label>
                                                        <Input
                                                            type="number"
                                                            step="0.1"
                                                            placeholder="Ex: 10"
                                                            value={dimensions.length}
                                                            onChange={(e) => setDimensions({...dimensions, length: e.target.value})}
                                                            data-testid="length-input"
                                                        />
                                                    </div>
                                                    <div className="space-y-2">
                                                        <Label>Largeur (mètres)</Label>
                                                        <Input
                                                            type="number"
                                                            step="0.1"
                                                            placeholder="Ex: 5"
                                                            value={dimensions.width}
                                                            onChange={(e) => setDimensions({...dimensions, width: e.target.value})}
                                                            data-testid="width-input"
                                                        />
                                                    </div>
                                                </div>
                                            </>
                                        )}
                                        <div className="space-y-2">
                                            <Label>Profondeur moyenne (mètres)</Label>
                                            <Input
                                                type="number"
                                                step="0.1"
                                                placeholder="Ex: 1.5"
                                                value={dimensions.depth}
                                                onChange={(e) => setDimensions({...dimensions, depth: e.target.value})}
                                                data-testid="depth-input"
                                            />
                                            <p className="text-xs text-muted-foreground">
                                                (Profondeur min + max) / 2
                                            </p>
                                        </div>
                                    </div>

                                    <Button 
                                        onClick={calculatePoolVolume} 
                                        className="w-full"
                                        data-testid="calculate-volume-btn"
                                    >
                                        <Calculator className="h-4 w-4 mr-2" />
                                        Calculer le volume
                                    </Button>

                                    {/* Result */}
                                    {calculatedVolume && (
                                        <div className="text-center py-6 bg-primary/5 rounded-xl" data-testid="volume-result">
                                            <p className="text-sm text-muted-foreground mb-1">Volume de votre piscine</p>
                                            <p className="text-4xl font-bold text-primary">
                                                {calculatedVolume} m³
                                            </p>
                                            <p className="text-sm text-muted-foreground mt-2">
                                                soit environ {Math.round(calculatedVolume * 1000)} litres
                                            </p>
                                            <div className="flex flex-col sm:flex-row gap-2 justify-center mt-4">
                                                <Button 
                                                    variant="outline" 
                                                    onClick={() => setActiveTab('dosage')}
                                                >
                                                    Calculer un dosage
                                                </Button>
                                                {user && (
                                                    <Button 
                                                        onClick={async () => {
                                                            try {
                                                                await apiRequest('/pool-config/save', {
                                                                    method: 'POST',
                                                                    body: JSON.stringify({
                                                                        volume: calculatedVolume,
                                                                        length: parseFloat(dimensions.length) || null,
                                                                        width: parseFloat(dimensions.width) || null,
                                                                        depth_avg: parseFloat(dimensions.depth) || null
                                                                    })
                                                                });
                                                                setPoolProfile(prev => ({
                                                                    ...prev,
                                                                    volume: calculatedVolume,
                                                                    length: parseFloat(dimensions.length) || null,
                                                                    width: parseFloat(dimensions.width) || null,
                                                                    depth_avg: parseFloat(dimensions.depth) || null
                                                                }));
                                                                toast.success('Volume enregistré dans votre profil !');
                                                            } catch (error) {
                                                                toast.error('Erreur lors de l\'enregistrement');
                                                            }
                                                        }}
                                                        data-testid="save-volume-btn"
                                                    >
                                                        <User className="h-4 w-4 mr-2" />
                                                        Enregistrer dans mon profil
                                                    </Button>
                                                )}
                                            </div>
                                            {!user && (
                                                <p className="text-xs text-muted-foreground mt-3">
                                                    <Link to="/auth" className="text-primary hover:underline">Connectez-vous</Link>
                                                    {' '}pour sauvegarder ce volume
                                                </p>
                                            )}
                                        </div>
                                    )}
                                </CardContent>
                            </Card>

                            {/* Common pool sizes reference */}
                            <Card className="glass">
                                <CardHeader>
                                    <CardTitle className="text-lg">Volumes courants de piscines</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <div className="grid gap-2 text-sm">
                                        <div className="flex justify-between py-2 border-b">
                                            <span className="text-muted-foreground">Petite piscine (6x3x1.2m)</span>
                                            <span className="font-medium">~22 m³</span>
                                        </div>
                                        <div className="flex justify-between py-2 border-b">
                                            <span className="text-muted-foreground">Piscine moyenne (8x4x1.5m)</span>
                                            <span className="font-medium">~48 m³</span>
                                        </div>
                                        <div className="flex justify-between py-2 border-b">
                                            <span className="text-muted-foreground">Grande piscine (10x5x1.5m)</span>
                                            <span className="font-medium">~75 m³</span>
                                        </div>
                                        <div className="flex justify-between py-2">
                                            <span className="text-muted-foreground">Piscine ronde (Ø 4.5m x 1.2m)</span>
                                            <span className="font-medium">~19 m³</span>
                                        </div>
                                    </div>
                                </CardContent>
                            </Card>
                        </TabsContent>
                    </Tabs>
                </div>
            </div>
        </>
    );
}
